import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { DriverForm } from "@/pages/admin/AdminDrivers";
import { KvRow, Panel, StatusBadge, btnSecondary, money } from "@/components/admin/ui";

export default function AdminDriverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    adminApi.get(`/drivers/${id}`).then((r) => setData(r.data))
      .catch(() => { toast.error("Chauffeur introuvable"); navigate("/admin/app/drivers"); });
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="h-96 animate-pulse rounded-md border border-slate/15 bg-white" />;
  const d = data.driver;
  const m = data.metrics;

  return (
    <div className="space-y-4" data-testid="admin-driver-detail">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => navigate(-1)} className={btnSecondary} data-testid="driver-back">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-ink" data-testid="driver-name">{d.first_name} {d.last_name}</h1>
          <p className="mt-0.5 text-sm text-slate">{d.type} · {d.status}</p>
        </div>
        <button type="button" onClick={() => setEditing(!editing)} className={btnSecondary} data-testid="driver-edit">
          {editing ? "Fermer" : "Modifier"}
        </button>
      </div>

      {editing && (
        <Panel title="Modifier le chauffeur" testId="driver-edit-panel">
          <DriverForm initial={d} onSaved={() => { setEditing(false); load(); }} onClose={() => setEditing(false)} />
        </Panel>
      )}

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-4">
          <Panel title="Contact" testId="driver-contact">
            <KvRow label="Téléphone"><a href={`tel:${d.phone}`} className="font-mono text-miles hover:underline">{d.phone}</a></KvRow>
            <KvRow label="E-mail">{d.email ? <a href={`mailto:${d.email}`} className="text-miles hover:underline">{d.email}</a> : "—"}</KvRow>
            <KvRow label="Adresse">{d.address || "—"}</KvRow>
          </Panel>
          <Panel title="Documents" testId="driver-docs">
            {(d.documents || []).length === 0 && <p className="text-sm text-slate">Aucun document enregistré.</p>}
            {(d.documents || []).map((doc, i) => {
              const expiring = doc.expires_at && doc.expires_at <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
              return (
                <div key={i} className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${expiring ? "border-amber-200 bg-amber-50" : "border-slate/15"}`}>
                  <span className="font-medium text-ink">{doc.type}</span>
                  <span className={`font-mono text-xs ${expiring ? "font-semibold text-amber-700" : "text-slate"}`}>expire {doc.expires_at}</span>
                </div>
              );
            })}
          </Panel>
          <Panel title="Compte chauffeur" testId="driver-finance">
            <KvRow label="Courses réalisées">{m.rides_completed}</KvRow>
            <KvRow label="CA généré"><span className="font-mono">{money(m.revenue_generated)}</span></KvRow>
            <KvRow label="Coût total"><span className="font-mono">{money(m.driver_cost_total)}</span></KvRow>
            <KvRow label="Solde dû"><span className="font-mono font-semibold text-ink" data-testid="driver-balance">{money(m.balance_due)}</span></KvRow>
          </Panel>
        </div>

        <div className="space-y-4 lg:col-span-8">
          <Panel title={`Prochaines missions (${data.upcoming.length})`} testId="driver-upcoming">
            {data.upcoming.length === 0 && <p className="text-sm text-slate">Aucune mission planifiée.</p>}
            <div className="divide-y divide-slate/10">
              {data.upcoming.map((b) => (
                <div key={b.id} onClick={() => navigate(`/admin/app/bookings/${b.id}`)} data-testid={`driver-ride-${b.tracking_code}`}
                  className="flex cursor-pointer items-center justify-between gap-3 py-2.5 text-sm hover:bg-miles-ice/30">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="font-mono text-xs text-slate">{b.date} {b.time}</span>
                    <span className="truncate text-ink">{b.pickup} → {b.dropoff || "à l'heure"}</span>
                  </span>
                  <StatusBadge status={b.display_status} />
                </div>
              ))}
            </div>
          </Panel>
          <Panel title={`Historique (${data.rides.length})`} testId="driver-history">
            <div className="divide-y divide-slate/10">
              {data.rides.map((b) => (
                <div key={b.id} onClick={() => navigate(`/admin/app/bookings/${b.id}`)}
                  className="flex cursor-pointer items-center justify-between gap-3 py-2 text-sm hover:bg-miles-ice/30">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="font-mono text-xs text-slate">{b.date}</span>
                    <span className="truncate text-ink">{b.pickup} → {b.dropoff || "à l'heure"}</span>
                  </span>
                  <span className="font-mono text-xs text-ink">{money(b.price_chf)}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
